/**
 * A dashboard's layout in miniature (screenshots/82 cards, 83 "Start from" tiles, 91 phone rows): every widget as a
 * small white box at its cells, with a glyph for what it shows (bars, a line, a number, a ring, seats, rows).
 * Decorative (aria-hidden); the card's title and meta carry the meaning.
 */
import type { Layout } from "../overview/grid/layoutEngine";
import { WIDGETS, widgetType } from "../overview/widgets/registry";

type Glyph = "bars" | "line" | "number" | "ring" | "seats" | "rows";

function glyphOf(i: Layout[number]): Glyph {
  const t = widgetType(i.type);
  const st = (i.config?.settings as { type?: string } | undefined)?.type;
  if (st) return st === "line" || st === "area" ? "line" : st === "table" ? "rows" : st === "number" ? "number" : st === "donut" ? "ring" : st === "seats" ? "seats" : "bars";
  if (!t) return "rows";
  if (t === "councilTension") return "ring";
  if (t === "whipCount" || t === "forecastWatch") return "seats";
  if (t === "number" || WIDGETS[t].archetype === "number") return "number";
  if (WIDGETS[t].archetype === "split" || WIDGETS[t].archetype === "chart") return "bars";
  return "rows";
}

const SEATS = ["b", "b", "b", "b", "b", "b", "b", "g", "g", "g", "o", "o", "o", "o", "o"];

function GlyphView({ g }: { g: Glyph }) {
  if (g === "bars") return <span className="an-thumb__bars">{[100, 72, 56, 38].map((w) => <i key={w} style={{ width: `${w}%` }} />)}</span>;
  if (g === "number") return <span className="an-thumb__number"><i /><b /></span>;
  if (g === "ring") return <span className="an-thumb__ring"><i /></span>;
  if (g === "seats") return <span className="an-thumb__seats">{SEATS.map((c, k) => <i key={k} data-c={c} />)}</span>;
  if (g === "line") {
    return (
      <svg className="an-thumb__line" viewBox="0 0 100 40" preserveAspectRatio="none">
        <polyline points="2,36 18,30 32,33 50,20 66,22 82,10 98,6" fill="none" vectorEffect="non-scaling-stroke" />
      </svg>
    );
  }
  return <span className="an-thumb__rows">{[0, 1, 2, 3].map((k) => <i key={k} />)}</span>;
}

/** `col`/`row`: px per grid cell in the thumbnail; the box keeps a 12-column grid. */
export function Thumb({ layout, col = 27, row = 13, gap = 4, className = "" }: { layout: Layout; col?: number; row?: number; gap?: number; className?: string }) {
  return (
    <span className={`an-thumb ${className}`} aria-hidden>
      <span className="an-thumb__grid" style={{ width: col * 12 - gap }}>
        {layout.map((i) => (
          <span key={i.id} className="an-thumb__box" style={{ left: i.x * col, top: i.y * row, width: i.w * col - gap, height: i.h * row - gap }}>
            <GlyphView g={glyphOf(i)} />
          </span>
        ))}
      </span>
    </span>
  );
}
